/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import config from 'js/app/config';
import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import { setLocation } from 'js/lib/coursera.redirect';

import { Button, Typography2 } from '@coursera/cds-core';

// eslint-disable-next-line no-restricted-imports
import ErrorPage from 'bundles/coursera-ui/components/rich/ErrorPage';
import logErrorToSentry from 'bundles/internal-common/utils/logErrorToSentry';
import { useNaptime } from 'bundles/naptimejs';
import ProgramSwitcherSelectionsV1 from 'bundles/naptimejs/resources/programSwitcherSelections.v1';

import _t from 'i18n!nls/unified-common';

type Props = {
  userId?: number;
};

export function OrgScopedArchivedDenyModal({ userId }: Props) {
  const { naptime } = useNaptime(() => ({}));
  const styles = {
    programArchivedDenyModal: css`
      display: flex;
      flex-direction: column;
      justify-content: center;
      min-width: 360px;
      max-width: 100%;
      padding: var(--cds-spacing-400);
      padding-bottom: 0;
      background-color: var(--cds-color-neutral-background-primary);
    `,

    buttonsWrapper: css`
      display: flex;
      flex-direction: row;
      justify-content: center;
      margin-top: var(--cds-spacing-400);
      margin-bottom: var(--cds-spacing-400);
    `,

    browseButton: css`
      margin-right: var(--cds-spacing-200);
    `,
  };

  const onBrowse = () => {
    if (userId && naptime) {
      naptime
        .executeMutation(
          ProgramSwitcherSelectionsV1.update(userId, {
            selectionType: 'COURSERA',
          })
        )
        .then(() => {
          setLocation('/browse');
        })
        .catch((error) => {
          logErrorToSentry(error);
          setLocation('/browse');
        });
    } else {
      setLocation('/browse');
    }
  };

  // TODO: We need better UX here - get confirmation from product team on the copy for this modal

  return (
    <ErrorPage disableHeader>
      <div css={styles.programArchivedDenyModal}>
        <Typography2 component="p">
          <FormattedHTMLMessage
            message={_t(
              'Unfortunately no content is currently available to learners in your organization. For more details please reach out to your Coursera learning administrator - this is the person who likely invited you to the organization or manages learning and development at your company. Rest assured that any course progress you have are saved, and you will be able to continue where you left off once your access is restored. If that is not an option, you can also purchase courses on your own to continue at {link}'
            )}
            link={<a href={config.url.base}>www.coursera.org</a>}
          />
        </Typography2>
        <div css={styles.buttonsWrapper}>
          <Button variant="secondary" onClick={onBrowse} css={styles.browseButton}>
            {_t('Browse Coursera')}
          </Button>
          <Button variant="primary" component="a" href="/enterprise" target="_blank">
            {_t('Enterprise')}
          </Button>
        </div>
      </div>
    </ErrorPage>
  );
}
